/**
 * API client for appointment type mapping endpoints
 */

import { authFetch, apiErrorMessage } from './authFetch';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

/**
 * Thrown when mapping a type onto a calendar would silently change which EMR
 * appointment type incoming GHL bookings on that calendar route back to.
 * Callers should let the user choose 'keep' or 'overwrite' and retry.
 */
export class ReverseMappingConflictError extends Error {
    calendarName: string;
    conflictingTypeName: string;

    constructor(calendarName: string, conflictingTypeName: string) {
        super(`Calendar "${calendarName}" already routes Advital bookings back to "${conflictingTypeName}".`);
        this.name = 'ReverseMappingConflictError';
        this.calendarName = calendarName;
        this.conflictingTypeName = conflictingTypeName;
    }
}

function parseReverseMappingConflict(status: number, errorBody: unknown): ReverseMappingConflictError | null {
    if (status !== 409 || !errorBody || typeof errorBody !== 'object') return null;
    const detail = (errorBody as { detail?: unknown }).detail;
    if (!detail || typeof detail !== 'object') return null;
    const d = detail as { error?: unknown; calendar_name?: unknown; conflicting_type_name?: unknown };
    if (d.error !== 'reverse_mapping_conflict') return null;
    return new ReverseMappingConflictError(String(d.calendar_name ?? ''), String(d.conflicting_type_name ?? ''));
}

export interface AppointmentTypeMappingSuggestion {
    id: number;
    gh_id: string;
    emr_source: string;
    type_external_id: string;
    type_name: string;
    subtype_external_id: string;
    subtype_name: string;
    suggested_calendar_id: number | null;
    suggested_calendar_name: string | null;
    suggested_calendar_external_id: string | null;
    confidence_score: number;
    reasoning: string;
    match_factors: {
        name_similarity?: string;
        keyword_matches?: string[];
        specialty_match?: boolean;
    } | null;
    alternative_suggestions: Array<{
        calendar_id: number;
        calendar_name: string;
        confidence_score: number;
        reasoning: string;
    }> | null;
    status: 'pending' | 'approved' | 'rejected' | 'engine_mapped';
    is_reviewed: boolean;
    reviewed_by: number | null;
    reviewed_by_name: string | null;
    reviewed_at: string | null;
    created_at: string;
    is_external?: boolean;
    /** Engine coverage: both | emr_to_ghl | ghl_to_emr */
    mapping_coverage?: 'both' | 'emr_to_ghl' | 'ghl_to_emr' | string | null;
}

export interface TriggerMappingRequest {
    gh_id: string;
}

export interface TriggerMappingResponse {
    status: string;
    message: string;
    suggestions_created: number;
}

export interface ReviewRequest {
    action: 'approve' | 'reject';
    notes?: string;
    reverse_mapping_choice?: 'keep' | 'overwrite';
}

export interface StatsResponse {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    high_confidence: number;
    medium_confidence: number;
    low_confidence: number;
}

export const appointmentTypeMappingApi = {
    /**
     * Trigger appointment type mapping for a GHL location
     */
    async triggerMapping(request: TriggerMappingRequest): Promise<TriggerMappingResponse> {
        const response = await authFetch(`${API_BASE_URL}/appointment-type-mapping/trigger/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to trigger mapping'));
        }

        return response.json();
    },

    /**
     * Get appointment type mapping suggestions
     */
    async getSuggestions(
        ghId: string,
        filters?: {
            status?: string;
            minConfidence?: number;
            maxConfidence?: number;
            limit?: number;
            offset?: number;
            calendarId?: string;
            typeName?: string;
            subtypeName?: string;
            calendarName?: string;
            search?: string;
        }
    ): Promise<{ total: number; suggestions: AppointmentTypeMappingSuggestion[] }> {
        const params = new URLSearchParams();
        if (filters?.status) params.append('status', filters.status);
        if (filters?.minConfidence !== undefined) params.append('min_confidence', filters.minConfidence.toString());
        if (filters?.maxConfidence !== undefined) params.append('max_confidence', filters.maxConfidence.toString());
        if (filters?.limit) params.append('limit', filters.limit.toString());
        if (filters?.offset) params.append('offset', filters.offset.toString());
        if (filters?.calendarId) params.append('calendar_id', filters.calendarId);
        if (filters?.typeName) params.append('type_name', filters.typeName);
        if (filters?.subtypeName) params.append('subtype_name', filters.subtypeName);
        if (filters?.calendarName) params.append('calendar_name', filters.calendarName);
        if (filters?.search) params.append('search', filters.search);

        const response = await authFetch(
            `${API_BASE_URL}/appointment-type-mapping/suggestions/${ghId}?${params.toString()}`
        );

        if (!response.ok) {
            throw new Error('Failed to fetch suggestions');
        }

        return response.json();
    },

    /**
     * Review a suggestion (approve or reject)
     */
    async reviewSuggestion(suggestionId: number, request: ReviewRequest): Promise<void> {
        const response = await authFetch(`${API_BASE_URL}/appointment-type-mapping/review/${suggestionId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            const conflict = parseReverseMappingConflict(response.status, error);
            if (conflict) throw conflict;
            throw new Error(apiErrorMessage(error, 'Failed to review suggestion'));
        }
    },

    /**
     * Permanently delete a mapping suggestion
     */
    async deleteSuggestion(suggestionId: number): Promise<void> {
        const response = await authFetch(`${API_BASE_URL}/appointment-type-mapping/${suggestionId}`, {
            method: 'DELETE',
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(apiErrorMessage(error, 'Failed to delete suggestion'));
        }
    },

    /**
     * Get distinct values per filterable column, for populating dropdown filters.
     */
    async getFilterOptions(ghId: string): Promise<{ type_name: string[]; subtype_name: string[]; calendar_name: string[] }> {
        const response = await authFetch(`${API_BASE_URL}/appointment-type-mapping/filter-options/${ghId}`);
        if (!response.ok) throw new Error('Failed to fetch filter options');
        return response.json();
    },

    /**
     * Get statistics for appointment type mappings
     */
    async getStats(ghId: string): Promise<StatsResponse> {
        const response = await authFetch(`${API_BASE_URL}/appointment-type-mapping/stats/${ghId}`);

        if (!response.ok) {
            throw new Error('Failed to fetch stats');
        }

        return response.json();
    },
};

// Manual mapping interfaces and methods
export interface GHLCalendar {
    id: number;
    name: string;
    description?: string;
    calendar_type?: string;
    is_active?: boolean;
}

export interface EMRAppointmentType {
    type_external_id: string;
    type_name: string;
    subtype_external_id: string;
    subtype_name: string;
}

export interface ManualAppointmentTypeMappingRequest {
    gh_id: string;
    type_external_id: string;
    type_name: string;
    subtype_external_id: string;
    subtype_name: string;
    calendar_id: number;
    calendar_name: string;
    cobalt_resource_ehr_id?: string;
    cobalt_provider_ehr_id?: string;
    reverse_mapping_choice?: 'keep' | 'overwrite';
}

export interface EMRAppointmentTypesResponse {
    combinations: EMRAppointmentType[];
    emr_source: string;
}

export const appointmentTypeMappingManualApi = {
    /**
     * Get all GHL calendars for manual mapping
     */
    async getGHLCalendars(ghId: string): Promise<GHLCalendar[]> {
        const response = await authFetch(`${API_BASE_URL}/appointment-type-mapping/ghl-calendars/${ghId}`);

        if (!response.ok) {
            throw new Error('Failed to fetch Advital calendars');
        }

        const data = await response.json();
        return data.calendars;
    },

    /**
     * Get all EMR appointment types for manual mapping
     */
    async getEMRAppointmentTypes(ghId: string): Promise<EMRAppointmentTypesResponse> {
        const response = await authFetch(`${API_BASE_URL}/appointment-type-mapping/emr-appointment-types/${ghId}`);

        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.detail || 'Failed to fetch EMR appointment types');
        }

        return response.json();
    },

    /**
     * Create a manual mapping (always inserts a new record)
     */
    async createManualMapping(request: ManualAppointmentTypeMappingRequest): Promise<void> {
        const response = await authFetch(`${API_BASE_URL}/appointment-type-mapping/manual`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            const conflict = parseReverseMappingConflict(response.status, error);
            if (conflict) throw conflict;
            throw new Error(apiErrorMessage(error, 'Failed to create manual mapping'));
        }
    },

    /**
     * Update the calendar on an existing mapping (called from the Edit button)
     */
    async updateManualMapping(
        suggestionId: number,
        calendarId: number,
        calendarName: string,
        reverseMappingChoice?: 'keep' | 'overwrite'
    ): Promise<void> {
        const response = await authFetch(`${API_BASE_URL}/appointment-type-mapping/manual/${suggestionId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                calendar_id: calendarId,
                calendar_name: calendarName,
                reverse_mapping_choice: reverseMappingChoice,
            }),
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            const conflict = parseReverseMappingConflict(response.status, error);
            if (conflict) throw conflict;
            throw new Error(apiErrorMessage(error, 'Failed to update mapping'));
        }
    },
};
